import type { Locale } from 'date-fns';
import { enUS, pl, de, es, hi, id, tr, ar, vi, th, ja, ko, fr, it, uk, cs, nl, hu, lt, ro, sv, bg, da, el, et, fi, hr, lv, sk, sl } from 'date-fns/locale';
import { docDateLocale } from '@/lib/docDateLocale';

// Type definitions
export type SupportedLanguage = 'en' | 'pl' | 'de' | 'pt-br' | 'pt-pt' | 'es' | 'hi' | 'id' | 'fil' | 'tr' | 'ar' | 'vi' | 'th' | 'ja' | 'ko' | 'fr' | 'it' | 'uk' | 'cs' | 'nl' | 'hu' | 'lt' | 'ro' | 'sv' | 'bg' | 'da' | 'el' | 'et' | 'fi' | 'hr' | 'lv' | 'sk' | 'sl';

// Month names for all supported languages
export const MONTH_NAMES: Record<SupportedLanguage, string[]> = {
  en: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],
  pl: [
    'stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
    'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'
  ],
  de: [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ],
  'pt-br': [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ],
  'pt-pt': [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ],
  es: [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ],
  hi: [
    'जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून',
    'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'
  ],
  id: [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ],
  fil: [
    'Enero', 'Pebrero', 'Marso', 'Abril', 'Mayo', 'Hunyo',
    'Hulyo', 'Agosto', 'Setyembre', 'Oktubre', 'Nobyembre', 'Disyembre'
  ],
  tr: [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ],
  ar: [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ],
  vi: [
    'Tháng Một', 'Tháng Hai', 'Tháng Ba', 'Tháng Tư', 'Tháng Năm', 'Tháng Sáu',
    'Tháng Bảy', 'Tháng Tám', 'Tháng Chín', 'Tháng Mười', 'Tháng Mười Một', 'Tháng Mười Hai'
  ],
  th: [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ],
  ja: [
    '1月', '2月', '3月', '4月', '5月', '6月',
    '7月', '8月', '9月', '10月', '11月', '12月'
  ],
  ko: [
    '1월', '2월', '3월', '4월', '5월', '6월',
    '7월', '8월', '9월', '10월', '11월', '12월'
  ],
  fr: [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
  ],
  it: [
    'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
    'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'
  ],
  uk: [
    'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
    'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'
  ],
  cs: [
    'ledna', 'února', 'března', 'dubna', 'května', 'června',
    'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'
  ],
  nl: [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december'
  ],
  hu: [
    'január', 'február', 'március', 'április', 'május', 'június',
    'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
  ],
  lt: [
    'sausio', 'vasario', 'kovo', 'balandžio', 'gegužės', 'birželio',
    'liepos', 'rugpjūčio', 'rugsėjo', 'spalio', 'lapkričio', 'gruodžio'
  ],
  ro: [
    'ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie',
    'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie'
  ],
  sv: [
    'januari', 'februari', 'mars', 'april', 'maj', 'juni',
    'juli', 'augusti', 'september', 'oktober', 'november', 'december'
  ],
  bg: [
    'януари', 'февруари', 'март', 'април', 'май', 'юни',
    'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'
  ],
  da: [
    'januar', 'februar', 'marts', 'april', 'maj', 'juni',
    'juli', 'august', 'september', 'oktober', 'november', 'december'
  ],
  el: [
    'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
    'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
  ],
  et: [
    'jaanuar', 'veebruar', 'märts', 'aprill', 'mai', 'juuni',
    'juuli', 'august', 'september', 'oktoober', 'november', 'detsember'
  ],
  fi: [
    'tammikuu', 'helmikuu', 'maaliskuu', 'huhtikuu', 'toukokuu', 'kesäkuu',
    'heinäkuu', 'elokuu', 'syyskuu', 'lokakuu', 'marraskuu', 'joulukuu'
  ],
  hr: [
    'siječanj', 'veljača', 'ožujak', 'travanj', 'svibanj', 'lipanj',
    'srpanj', 'kolovoz', 'rujan', 'listopad', 'studeni', 'prosinac'
  ],
  lv: [
    'janvāris', 'februāris', 'marts', 'aprīlis', 'maijs', 'jūnijs',
    'jūlijs', 'augusts', 'septembris', 'oktobris', 'novembris', 'decembris'
  ],
  sk: [
    'január', 'február', 'marec', 'apríl', 'máj', 'jún',
    'júl', 'august', 'september', 'október', 'november', 'december'
  ],
  sl: [
    'januar', 'februar', 'marec', 'april', 'maj', 'junij',
    'julij', 'avgust', 'september', 'oktober', 'november', 'december'
  ],
};

// Map language codes to date-fns locales
// Note: date-fns has limited locale support, so we use closest available locales
// Portuguese Brazil uses Spanish locale as it's Romance language with similar structure
export const DATE_LOCALES: Record<SupportedLanguage, Locale> = {
  en: enUS,
  pl: pl,
  de: de,
  es: es,
  hi: hi,
  id: id,
  'pt-br': es, // Portuguese Brazil uses Spanish locale as fallback (similar Romance language)
  'pt-pt': es, // Portuguese Portugal uses Spanish locale as fallback (similar Romance language)
  fil: id, // Filipino uses Indonesian locale as fallback (similar structure)
  tr: tr,
  ar: ar,
  vi: vi,
  th: th,
  ja: ja,
  ko: ko,
  fr: fr,
  it: it,
  uk: uk,
  cs: cs,
  nl: nl,
  hu: hu,
  lt: lt,
  ro: ro,
  sv: sv,
  bg: bg,
  da: da,
  el: el,
  et: et,
  fi: fi,
  hr: hr,
  lv: lv,
  sk: sk,
  sl: sl,
};

// Map language codes to locale strings for toLocaleDateString
export const LOCALE_STRINGS: Record<SupportedLanguage, string> = {
  en: 'en-US',
  pl: 'pl-PL',
  de: 'de-DE',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-PT',
  es: 'es-ES',
  hi: 'hi-IN',
  id: 'id-ID',
  fil: 'fil-PH',
  tr: 'tr-TR',
  ar: 'ar-SA',
  vi: 'vi-VN',
  th: 'th-TH',
  ja: 'ja-JP',
  ko: 'ko-KR',
  fr: 'fr-FR',
  it: 'it-IT',
  uk: 'uk-UA',
  cs: 'cs-CZ',
  nl: 'nl-NL',
  hu: 'hu-HU',
  lt: 'lt-LT',
  ro: 'ro-RO',
  sv: 'sv-SE',
  bg: 'bg-BG',
  da: 'da-DK',
  el: 'el-GR',
  et: 'et-EE',
  fi: 'fi-FI',
  hr: 'hr-HR',
  lv: 'lv-LV',
  sk: 'sk-SK',
  sl: 'sl-SI',
};

// Map language codes to Accept-Language header values for APIs
export const ACCEPT_LANGUAGE_HEADERS: Record<SupportedLanguage, string> = {
  en: 'en',
  pl: 'pl',
  de: 'de',
  'pt-br': 'pt-BR',
  'pt-pt': 'pt-PT',
  es: 'es',
  hi: 'hi',
  id: 'id',
  fil: 'tl', // Tagalog/Filipino is 'tl' in language codes
  tr: 'tr',
  ar: 'ar',
  vi: 'vi',
  th: 'th',
  ja: 'ja',
  ko: 'ko',
  fr: 'fr',
  it: 'it',
  uk: 'uk',
  cs: 'cs',
  nl: 'nl',
  hu: 'hu',
  lt: 'lt',
  ro: 'ro',
  sv: 'sv',
  bg: 'bg',
  da: 'da',
  el: 'el',
  et: 'et',
  fi: 'fi',
  hr: 'hr',
  lv: 'lv',
  sk: 'sk',
  sl: 'sl',
};

// Map language codes to display names
export const LANGUAGE_DISPLAY_NAMES: Record<SupportedLanguage, string> = {
  en: 'EN English',
  pl: 'PL Polski',
  de: 'DE Deutsch',
  'pt-br': 'PT Português (BR)',
  'pt-pt': 'PT Português (PT)',
  es: 'ES Español',
  hi: 'HI हिन्दी',
  id: 'ID Bahasa',
  fil: 'FIL Tagalog',
  tr: 'TR Türkçe',
  ar: 'AR العربية',
  vi: 'VI Tiếng Việt',
  th: 'TH ไทย',
  ja: 'JA 日本語',
  ko: 'KO 한국어',
  fr: 'FR Français',
  it: 'IT Italiano',
  uk: 'UK Українська',
  cs: 'CS Čeština',
  nl: 'NL Nederlands',
  hu: 'HU Magyar',
  lt: 'LT Lietuvių',
  ro: 'RO Română',
  sv: 'SV Svenska',
  bg: 'BG Български',
  da: 'DA Dansk',
  el: 'EL Ελληνικά',
  et: 'ET Eesti',
  fi: 'FI Suomi',
  hr: 'HR Hrvatski',
  lv: 'LV Latviešu',
  sk: 'SK Slovenčina',
  sl: 'SL Slovenščina',
};

// Normalize language code to standard format
export const normalizeLanguageCode = (code: string | null | undefined): SupportedLanguage | null => {
  if (!code) return null;

  const normalized = code.toLowerCase().replace('_', '-');

  // Handle Portuguese variants — distinguish pt-PT (Portugal/EU) from pt-BR (Brazil)
  if (normalized === 'pt-pt' || normalized === 'pt-portugal') {
    return 'pt-pt';
  }
  if (normalized === 'pt' || normalized.startsWith('pt-')) {
    return 'pt-br';
  }

  if (isSupportedLanguage(normalized)) {
    return normalized as SupportedLanguage;
  }

  return null;
};

// Check if a language code is supported
export const isSupportedLanguage = (code: string): code is SupportedLanguage => {
  return ['en', 'pl', 'de', 'pt-br', 'pt-pt', 'es', 'hi', 'id', 'fil', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt', 'ro', 'sv', 'bg', 'da', 'el', 'et', 'fi', 'hr', 'lv', 'sk', 'sl'].includes(code);
};

// Get month names for a language
export const getMonthNames = (language: SupportedLanguage): string[] => {
  return MONTH_NAMES[language] || MONTH_NAMES.en;
};

// Get date locale for a language
export const getDateLocale = (language: SupportedLanguage) => {
  return DATE_LOCALES[language] || DATE_LOCALES.en;
};

// Get locale string for toLocaleDateString. English follows the browser's
// region: a fixed en-US printed 10/14/2026 for a reader in London or Sydney.
export const getLocaleString = (language: SupportedLanguage): string => {
  if (language === 'en' || !LOCALE_STRINGS[language]) return docDateLocale('en');
  return LOCALE_STRINGS[language];
};

// Get Accept-Language header value
export const getAcceptLanguageHeader = (language: SupportedLanguage): string => {
  return ACCEPT_LANGUAGE_HEADERS[language] || ACCEPT_LANGUAGE_HEADERS.en;
};

// Get display name for a language
export const getLanguageDisplayName = (language: SupportedLanguage): string => {
  return LANGUAGE_DISPLAY_NAMES[language] || language;
};

// Format date with month name in a specific language
export const formatDateWithMonthName = (date: Date, language: SupportedLanguage): string => {
  const monthNames = getMonthNames(language);
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Get all supported languages
export const getAllSupportedLanguages = (): SupportedLanguage[] => {
  return ['en', 'pl', 'de', 'pt-br', 'pt-pt', 'es', 'hi', 'id', 'fil', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl', 'hu', 'lt', 'ro', 'sv', 'bg', 'da', 'el', 'et', 'fi', 'hr', 'lv', 'sk', 'sl'];
};

// PDF text translations for all languages
export const PDF_TEXT_TRANSLATIONS: Record<SupportedLanguage, Record<string, string>> = {
  en: {
    offerValidUntil: 'Offer valid until:',
    placementPlan: 'Placement Plan',
    cameras: 'Cameras',
    recommendations: 'Recommendations:',
    notes: 'Notes:',
    offerPreparedBy: 'Offer prepared by',
    pricesChangeNotice: 'Prices may change after the offer validity date.',
    yourCompany: 'Your Company',
    no: 'No.',
    productService: 'Product/Service',
    qty: 'Qty',
    unitNet: 'Unit Net',
    unitGross: 'Unit Gross',
    totalNet: 'Total Net',
    totalGross: 'Total Gross',
    subtotalNet: 'Subtotal Net:',
    vat: 'VAT:',
    total: 'TOTAL',
    net: 'NET',
    gross: 'GROSS',
    item: 'Item',
    position: 'Position',
    clientData: 'CLIENT DATA',
    clientDetails: 'Client Details',
    projectInformation: 'Project Information',
    pricingVariants: 'Pricing Variants',
    equipmentAndMaterials: 'Equipment & Materials',
    device: 'Device',
    quantity: 'Quantity',
    price: 'Price',
    date: 'Date:',
    company: 'Company:',
    contact: 'Contact:',
    email: 'Email:',
    phone: 'Phone:',
    project: 'Project:',
    fences: 'Fences:',
    buildings: 'Buildings:',
    devices: 'Devices:',
    basePrice: 'Base price:',
    perCamera: 'Per camera:',
    page: 'Page',
    of: 'of',
    monthlyFee: 'Monthly fee:',
    contractPeriod: 'Contract period:',
    indefinite: 'indefinite',
    freeConnection: 'Free connection:',
    connectionCost: 'Connection cost:',
    yes: 'Yes',
    netSuffix: 'net',
    fovNote: 'FOV ranges shown are approximate and may vary based on installation conditions.',
  },
  pl: {
    offerValidUntil: 'Oferta ważna do:',
    placementPlan: 'Plan rozmieszczenia',
    cameras: 'Kamery',
    recommendations: 'Zalecenia:',
    notes: 'Uwagi:',
    offerPreparedBy: 'Oferta przygotowana przez',
    pricesChangeNotice: 'Ceny po terminie ważności oferty mogą ulec zmianie.',
    yourCompany: 'Twoja Firma',
    no: 'Lp.',
    productService: 'Nazwa produktu/usługi',
    qty: 'Szt.',
    unitNet: 'Cena netto',
    unitGross: 'Cena brutto',
    totalNet: 'Wart. netto',
    totalGross: 'Wart. brutto',
    subtotalNet: 'Suma netto:',
    vat: 'VAT:',
    total: 'RAZEM',
    net: 'netto',
    gross: 'brutto',
    item: 'Pozycja',
    position: 'Pozycja',
    clientData: 'DANE KLIENTA',
    clientDetails: 'Dane Klienta',
    projectInformation: 'Informacje o Projekcie',
    pricingVariants: 'Warianty Cenowe',
    equipmentAndMaterials: 'Urządzenia i Materiały',
    device: 'Urządzenie',
    quantity: 'Ilość',
    price: 'Cena',
    date: 'Data:',
    company: 'Firma:',
    contact: 'Kontakt:',
    email: 'Email:',
    phone: 'Telefon:',
    project: 'Projekt:',
    fences: 'Ogrodzenia:',
    buildings: 'Budynki:',
    devices: 'Urządzenia:',
    basePrice: 'Cena bazowa:',
    perCamera: 'Za kamerę:',
    page: 'Strona',
    of: 'z',
    monthlyFee: 'Opłata miesięczna:',
    contractPeriod: 'Okres umowy:',
    indefinite: 'czas nieokreślony',
    freeConnection: 'Bezpłatne podłączenie:',
    connectionCost: 'Koszt podłączenia:',
    yes: 'Tak',
    netSuffix: 'netto',
    fovNote: 'Zasięgi pola widzenia (FOV) są przybliżone i mogą się różnić w zależności od warunków instalacji.',
  },
  de: {
    offerValidUntil: 'Angebot gültig bis:',
    placementPlan: 'Platzierungsplan',
    cameras: 'Kameras',
    recommendations: 'Empfehlungen:',
    notes: 'Anmerkungen:',
    offerPreparedBy: 'Angebot erstellt von',
    pricesChangeNotice: 'Preise können sich nach dem Angebotsgültigkeitsdatum ändern.',
    yourCompany: 'Ihr Unternehmen',
    no: 'Nr.',
    productService: 'Produkt/Dienstleistung',
    qty: 'Mge.',
    unitNet: 'Nettoprice',
    unitGross: 'Bruttoprice',
    totalNet: 'Summe Netto',
    totalGross: 'Summe Brutto',
    subtotalNet: 'Summe Netto:',
    vat: 'MwSt:',
    total: 'GESAMT',
    net: 'Netto',
    gross: 'Brutto',
    item: 'Artikel',
    position: 'Position',
    clientData: 'KUNDENDATEN',
    clientDetails: 'Kundendaten',
    projectInformation: 'Projektinformationen',
    pricingVariants: 'Preisvarianten',
    equipmentAndMaterials: 'Geräte & Materialien',
    device: 'Gerät',
    quantity: 'Menge',
    price: 'Preis',
    date: 'Datum:',
    company: 'Firma:',
    contact: 'Kontakt:',
    email: 'E-Mail:',
    phone: 'Telefon:',
    project: 'Projekt:',
    fences: 'Zäune:',
    buildings: 'Gebäude:',
    devices: 'Geräte:',
    basePrice: 'Grundpreis:',
    perCamera: 'Pro Kamera:',
    page: 'Seite',
    of: 'von',
    monthlyFee: 'Monatliche Gebühr:',
    contractPeriod: 'Vertragslaufzeit:',
    indefinite: 'unbefristet',
    freeConnection: 'Kostenloser Anschluss:',
    connectionCost: 'Anschlusskosten:',
    yes: 'Ja',
    netSuffix: 'netto',
    fovNote: 'Die dargestellten FOV-Reichweiten sind Näherungswerte und können je nach Installationsbedingungen variieren.',
  },
  'pt-br': {
    offerValidUntil: 'Oferta válida até:',
    placementPlan: 'Plano de Colocação',
    cameras: 'Câmeras',
    recommendations: 'Recomendações:',
    notes: 'Notas:',
    offerPreparedBy: 'Oferta preparada por',
    pricesChangeNotice: 'Os preços podem mudar após a data de validade da oferta.',
    yourCompany: 'Sua Empresa',
    no: 'Nº',
    productService: 'Produto/Serviço',
    qty: 'Qtd',
    unitNet: 'Preço Unitário Líquido',
    unitGross: 'Preço Unitário Bruto',
    totalNet: 'Total Líquido',
    totalGross: 'Total Bruto',
    subtotalNet: 'Subtotal Líquido:',
    vat: 'IVA:',
    total: 'TOTAL',
    net: 'Líquido',
    gross: 'Bruto',
    item: 'Item',
    position: 'Posição',
    clientData: 'DADOS DO CLIENTE',
    clientDetails: 'Dados do Cliente',
    projectInformation: 'Informações do Projeto',
    pricingVariants: 'Variantes de Preço',
    equipmentAndMaterials: 'Equipamentos e Materiais',
    device: 'Dispositivo',
    quantity: 'Quantidade',
    price: 'Preço',
    date: 'Data:',
    company: 'Empresa:',
    contact: 'Contato:',
    email: 'E-mail:',
    phone: 'Telefone:',
    project: 'Projeto:',
    fences: 'Cercas:',
    buildings: 'Edifícios:',
    devices: 'Dispositivos:',
    basePrice: 'Preço base:',
    perCamera: 'Por câmera:',
    page: 'Página',
    of: 'de',
    monthlyFee: 'Taxa mensal:',
    contractPeriod: 'Período do contrato:',
    indefinite: 'indeterminado',
    freeConnection: 'Conexão gratuita:',
    connectionCost: 'Custo de conexão:',
    yes: 'Sim',
    netSuffix: 'líquido',
    fovNote: 'Os alcances de FOV mostrados são aproximados e podem variar de acordo com as condições de instalação.',
  },
  'pt-pt': {
    offerValidUntil: 'Proposta válida até:',
    placementPlan: 'Plano de Colocação',
    cameras: 'Câmaras',
    recommendations: 'Recomendações:',
    notes: 'Notas:',
    offerPreparedBy: 'Proposta elaborada por',
    pricesChangeNotice: 'Os preços podem ser alterados após a data de validade da proposta.',
    yourCompany: 'A Sua Empresa',
    no: 'N.º',
    productService: 'Produto/Serviço',
    qty: 'Qtd.',
    unitNet: 'Preço Unitário Líquido',
    unitGross: 'Preço Unitário Bruto',
    totalNet: 'Total Líquido',
    totalGross: 'Total Bruto',
    subtotalNet: 'Subtotal Líquido:',
    vat: 'IVA:',
    total: 'TOTAL',
    net: 'Líquido',
    gross: 'Bruto',
    item: 'Item',
    position: 'Posição',
    clientData: 'DADOS DO CLIENTE',
    clientDetails: 'Dados do Cliente',
    projectInformation: 'Informações do Projeto',
    pricingVariants: 'Variantes de Preço',
    equipmentAndMaterials: 'Equipamentos e Materiais',
    device: 'Dispositivo',
    quantity: 'Quantidade',
    price: 'Preço',
    date: 'Data:',
    company: 'Empresa:',
    contact: 'Contacto:',
    email: 'E-mail:',
    phone: 'Telefone:',
    project: 'Projeto:',
    fences: 'Vedações:',
    buildings: 'Edifícios:',
    devices: 'Dispositivos:',
    basePrice: 'Preço base:',
    perCamera: 'Por câmara:',
    page: 'Página',
    of: 'de',
    monthlyFee: 'Mensalidade:',
    contractPeriod: 'Período do contrato:',
    indefinite: 'indeterminado',
    freeConnection: 'Ligação gratuita:',
    connectionCost: 'Custo de ligação:',
    yes: 'Sim',
    netSuffix: 'líquido',
    fovNote: 'Os alcances de FOV apresentados são aproximados e podem variar consoante as condições de instalação.',
  },
  es: {
    offerValidUntil: 'Oferta válida hasta:',
    placementPlan: 'Plan de Colocación',
    cameras: 'Cámaras',
    recommendations: 'Recomendaciones:',
    notes: 'Notas:',
    offerPreparedBy: 'Oferta preparada por',
    pricesChangeNotice: 'Los precios pueden cambiar después de la fecha de validez de la oferta.',
    yourCompany: 'Su Empresa',
    no: 'Nº',
    productService: 'Producto/Servicio',
    qty: 'Ctd',
    unitNet: 'Precio Unitario Neto',
    unitGross: 'Precio Unitario Bruto',
    totalNet: 'Total Neto',
    totalGross: 'Total Bruto',
    subtotalNet: 'Subtotal Neto:',
    vat: 'IVA:',
    total: 'TOTAL',
    net: 'Neto',
    gross: 'Bruto',
    item: 'Artículo',
    position: 'Posición',
    clientData: 'DATOS DEL CLIENTE',
    clientDetails: 'Datos del Cliente',
    projectInformation: 'Información del Proyecto',
    pricingVariants: 'Variantes de Precio',
    equipmentAndMaterials: 'Equipos y Materiales',
    device: 'Dispositivo',
    quantity: 'Cantidad',
    price: 'Precio',
    date: 'Fecha:',
    company: 'Empresa:',
    contact: 'Contacto:',
    email: 'Correo:',
    phone: 'Teléfono:',
    project: 'Proyecto:',
    fences: 'Cercas:',
    buildings: 'Edificios:',
    devices: 'Dispositivos:',
    basePrice: 'Precio base:',
    perCamera: 'Por cámara:',
    page: 'Página',
    of: 'de',
    monthlyFee: 'Cuota mensual:',
    contractPeriod: 'Período del contrato:',
    indefinite: 'indefinido',
    freeConnection: 'Conexión gratuita:',
    connectionCost: 'Costo de conexión:',
    yes: 'Sí',
    netSuffix: 'neto',
    fovNote: 'Los rangos de FOV mostrados son aproximados y pueden variar según las condiciones de instalación.',
  },
  hi: {
    offerValidUntil: 'प्रस्ताव वैध है तक:',
    placementPlan: 'प्लेसमेंट योजना',
    cameras: 'कैमरे',
    recommendations: 'सिफारिशें:',
    notes: 'नोट्स:',
    offerPreparedBy: 'द्वारा तैयार किया गया प्रस्ताव',
    pricesChangeNotice: 'प्रस्ताव वैधता तिथि के बाद कीमतें बदल सकती हैं।',
    yourCompany: 'आपकी कंपनी',
    no: 'क्र.',
    productService: 'उत्पाद/सेवा',
    qty: 'मात्रा',
    unitNet: 'इकाई नेट',
    unitGross: 'इकाई सकल',
    totalNet: 'कुल नेट',
    totalGross: 'कुल सकल',
    subtotalNet: 'योग नेट:',
    vat: 'वैट:',
    total: 'कुल',
    net: 'नेट',
    gross: 'सकल',
    item: 'वस्तु',
    position: 'स्थिति',
    clientData: 'ग्राहक डेटा',
    clientDetails: 'ग्राहक विवरण',
    projectInformation: 'परियोजना जानकारी',
    pricingVariants: 'मूल्य विकल्प',
    equipmentAndMaterials: 'उपकरण और सामग्री',
    device: 'उपकरण',
    quantity: 'मात्रा',
    price: 'कीमत',
    date: 'तारीख:',
    company: 'कंपनी:',
    contact: 'संपर्क:',
    email: 'ईमेल:',
    phone: 'फोन:',
    project: 'परियोजना:',
    fences: 'बाड़:',
    buildings: 'भवन:',
    devices: 'उपकरण:',
    basePrice: 'आधार मूल्य:',
    perCamera: 'प्रति कैमरा:',
    page: 'पृष्ठ',
    of: 'का',
    monthlyFee: 'मासिक शुल्क:',
    contractPeriod: 'अनुबंध अवधि:',
    indefinite: 'अनिश्चित',
    freeConnection: 'मुफ्त कनेक्शन:',
    connectionCost: 'कनेक्शन लागत:',
    yes: 'हाँ',
    netSuffix: 'नेट',
    fovNote: 'दिखाई गई FOV सीमाएँ अनुमानित हैं और स्थापना की स्थिति के अनुसार भिन्न हो सकती हैं।',
  },
  id: {
    offerValidUntil: 'Penawaran berlaku hingga:',
    placementPlan: 'Rencana Penempatan',
    cameras: 'Kamera',
    recommendations: 'Rekomendasi:',
    notes: 'Catatan:',
    offerPreparedBy: 'Penawaran disiapkan oleh',
    pricesChangeNotice: 'Harga dapat berubah setelah tanggal berlakunya penawaran.',
    yourCompany: 'Perusahaan Anda',
    no: 'No.',
    productService: 'Produk/Layanan',
    qty: 'Jml.',
    unitNet: 'Harga Unit Bersih',
    unitGross: 'Harga Unit Kotor',
    totalNet: 'Total Bersih',
    totalGross: 'Total Kotor',
    subtotalNet: 'Subtotal Bersih:',
    vat: 'PPN:',
    total: 'TOTAL',
    net: 'Bersih',
    gross: 'Kotor',
    item: 'Item',
    position: 'Posisi',
    clientData: 'DATA KLIEN',
    clientDetails: 'Data Klien',
    projectInformation: 'Informasi Proyek',
    pricingVariants: 'Varian Harga',
    equipmentAndMaterials: 'Peralatan & Material',
    device: 'Perangkat',
    quantity: 'Jumlah',
    price: 'Harga',
    date: 'Tanggal:',
    company: 'Perusahaan:',
    contact: 'Kontak:',
    email: 'Email:',
    phone: 'Telepon:',
    project: 'Proyek:',
    fences: 'Pagar:',
    buildings: 'Bangunan:',
    devices: 'Perangkat:',
    basePrice: 'Harga dasar:',
    perCamera: 'Per kamera:',
    page: 'Halaman',
    of: 'dari',
    monthlyFee: 'Biaya bulanan:',
    contractPeriod: 'Periode kontrak:',
    indefinite: 'tidak terbatas',
    freeConnection: 'Koneksi gratis:',
    connectionCost: 'Biaya koneksi:',
    yes: 'Ya',
    netSuffix: 'bersih',
    fovNote: 'Jangkauan FOV yang ditampilkan adalah perkiraan dan dapat bervariasi berdasarkan kondisi instalasi.',
  },
  fil: {
    offerValidUntil: 'Ang alok ay valid hanggang:',
    placementPlan: 'Plano ng Paglalagay',
    cameras: 'Mga Kamera',
    recommendations: 'Mga Rekomendasyon:',
    notes: 'Mga Tala:',
    offerPreparedBy: 'Alok na inihanda ng',
    pricesChangeNotice: 'Ang mga presyo ay maaaring magbago pagkatapos ng petsa ng pag-expire ng alok.',
    yourCompany: 'Ang Iyong Kumpanya',
    no: 'No.',
    productService: 'Produkto/Serbisyo',
    qty: 'Qtd.',
    unitNet: 'Unit Net',
    unitGross: 'Unit Gross',
    totalNet: 'Total Net',
    totalGross: 'Total Gross',
    subtotalNet: 'Subtotal Net:',
    vat: 'VAT:',
    total: 'KABUUAN',
    net: 'Net',
    gross: 'Gross',
    item: 'Artikulo',
    position: 'Posisyon',
    clientData: 'DATA NG KLIYENTE',
    clientDetails: 'Datos ng Kliyente',
    projectInformation: 'Impormasyon ng Proyekto',
    pricingVariants: 'Mga Variant ng Presyo',
    equipmentAndMaterials: 'Kagamitan at Materyales',
    device: 'Aparato',
    quantity: 'Bilang',
    price: 'Presyo',
    date: 'Petsa:',
    company: 'Kumpanya:',
    contact: 'Kontak:',
    email: 'Email:',
    phone: 'Telepono:',
    project: 'Proyekto:',
    fences: 'Mga Bakod:',
    buildings: 'Mga Gusali:',
    devices: 'Mga Aparato:',
    basePrice: 'Batayang presyo:',
    perCamera: 'Bawat kamera:',
    page: 'Pahina',
    of: 'ng',
    monthlyFee: 'Buwanang bayad:',
    contractPeriod: 'Panahon ng kontrata:',
    indefinite: 'walang takdang panahon',
    freeConnection: 'Libreng koneksyon:',
    connectionCost: 'Gastos sa koneksyon:',
    yes: 'Oo',
    netSuffix: 'net',
    fovNote: 'Ang mga saklaw ng FOV na ipinapakita ay tantiya lamang at maaaring mag-iba depende sa kondisyon ng instalasyon.',
  },
  tr: {
    offerValidUntil: 'Teklif geçerlilik tarihi:',
    placementPlan: 'Yerleşim Planı',
    cameras: 'Kameralar',
    recommendations: 'Öneriler:',
    notes: 'Notlar:',
    offerPreparedBy: 'Teklifi hazırlayan',
    pricesChangeNotice: 'Fiyatlar teklif geçerlilik tarihinden sonra değişebilir.',
    yourCompany: 'Şirketiniz',
    no: 'No.',
    productService: 'Ürün/Hizmet',
    qty: 'Adet',
    unitNet: 'Birim Net',
    unitGross: 'Birim Brüt',
    totalNet: 'Toplam Net',
    totalGross: 'Toplam Brüt',
    subtotalNet: 'Ara Toplam Net:',
    vat: 'KDV:',
    total: 'TOPLAM',
    net: 'Net',
    gross: 'Brüt',
    item: 'Kalem',
    position: 'Pozisyon',
    clientData: 'MÜŞTERİ BİLGİLERİ',
    clientDetails: 'Müşteri Bilgileri',
    projectInformation: 'Proje Bilgileri',
    pricingVariants: 'Fiyat Seçenekleri',
    equipmentAndMaterials: 'Ekipman ve Malzemeler',
    device: 'Cihaz',
    quantity: 'Miktar',
    price: 'Fiyat',
    date: 'Tarih:',
    company: 'Şirket:',
    contact: 'İletişim:',
    email: 'E-posta:',
    phone: 'Telefon:',
    project: 'Proje:',
    fences: 'Çitler:',
    buildings: 'Binalar:',
    devices: 'Cihazlar:',
    basePrice: 'Taban fiyat:',
    perCamera: 'Kamera başına:',
    page: 'Sayfa',
    of: '/',
    monthlyFee: 'Aylık ücret:',
    contractPeriod: 'Sözleşme süresi:',
    indefinite: 'belirsiz',
    freeConnection: 'Ücretsiz bağlantı:',
    connectionCost: 'Bağlantı maliyeti:',
    yes: 'Evet',
    netSuffix: 'net',
    fovNote: 'Gösterilen FOV aralıkları yaklaşıktır ve kurulum koşullarına göre değişebilir.',
  },
  ar: {
    offerValidUntil: 'العرض صالح حتى:',
    placementPlan: 'خطة التوزيع',
    cameras: 'الكاميرات',
    recommendations: 'التوصيات:',
    notes: 'ملاحظات:',
    offerPreparedBy: 'العرض مُعد من قبل',
    pricesChangeNotice: 'قد تتغير الأسعار بعد تاريخ انتهاء صلاحية العرض.',
    yourCompany: 'شركتك',
    no: 'رقم',
    productService: 'المنتج/الخدمة',
    qty: 'الكمية',
    unitNet: 'سعر الوحدة الصافي',
    unitGross: 'سعر الوحدة الإجمالي',
    totalNet: 'الإجمالي الصافي',
    totalGross: 'الإجمالي الكلي',
    subtotalNet: 'المجموع الفرعي الصافي:',
    vat: 'ضريبة القيمة المضافة:',
    total: 'الإجمالي',
    net: 'صافي',
    gross: 'إجمالي',
    item: 'بند',
    position: 'موضع',
    clientData: 'بيانات العميل',
    clientDetails: 'تفاصيل العميل',
    projectInformation: 'معلومات المشروع',
    pricingVariants: 'خيارات التسعير',
    equipmentAndMaterials: 'المعدات والمواد',
    device: 'جهاز',
    quantity: 'الكمية',
    price: 'السعر',
    date: 'التاريخ:',
    company: 'الشركة:',
    contact: 'جهة الاتصال:',
    email: 'البريد الإلكتروني:',
    phone: 'الهاتف:',
    project: 'المشروع:',
    fences: 'الأسوار:',
    buildings: 'المباني:',
    devices: 'الأجهزة:',
    basePrice: 'السعر الأساسي:',
    perCamera: 'لكل كاميرا:',
    page: 'صفحة',
    of: 'من',
    monthlyFee: 'الرسوم الشهرية:',
    contractPeriod: 'مدة العقد:',
    indefinite: 'غير محدد',
    freeConnection: 'توصيل مجاني:',
    connectionCost: 'تكلفة التوصيل:',
    yes: 'نعم',
    netSuffix: 'صافي',
    fovNote: 'نطاقات زاوية الرؤية المعروضة تقريبية وقد تختلف حسب ظروف التركيب.',
  },
  vi: {
    offerValidUntil: 'Báo giá có hiệu lực đến:',
    placementPlan: 'Sơ đồ bố trí',
    cameras: 'Camera',
    recommendations: 'Khuyến nghị:',
    notes: 'Ghi chú:',
    offerPreparedBy: 'Báo giá được chuẩn bị bởi',
    pricesChangeNotice: 'Giá có thể thay đổi sau ngày hết hạn báo giá.',
    yourCompany: 'Công ty của bạn',
    no: 'STT',
    productService: 'Sản phẩm/Dịch vụ',
    qty: 'SL',
    unitNet: 'Đơn giá trước thuế',
    unitGross: 'Đơn giá sau thuế',
    totalNet: 'Tổng trước thuế',
    totalGross: 'Tổng sau thuế',
    subtotalNet: 'Tạm tính trước thuế:',
    vat: 'VAT:',
    total: 'TỔNG CỘNG',
    net: 'Trước thuế',
    gross: 'Sau thuế',
    item: 'Mục',
    position: 'Vị trí',
    clientData: 'THÔNG TIN KHÁCH HÀNG',
    clientDetails: 'Chi tiết khách hàng',
    projectInformation: 'Thông tin dự án',
    pricingVariants: 'Phương án giá',
    equipmentAndMaterials: 'Thiết bị & Vật liệu',
    device: 'Thiết bị',
    quantity: 'Số lượng',
    price: 'Giá',
    date: 'Ngày:',
    company: 'Công ty:',
    contact: 'Liên hệ:',
    email: 'Email:',
    phone: 'Điện thoại:',
    project: 'Dự án:',
    fences: 'Hàng rào:',
    buildings: 'Tòa nhà:',
    devices: 'Thiết bị:',
    basePrice: 'Giá cơ bản:',
    perCamera: 'Mỗi camera:',
    page: 'Trang',
    of: '/',
    monthlyFee: 'Phí hàng tháng:',
    contractPeriod: 'Thời hạn hợp đồng:',
    indefinite: 'không xác định',
    freeConnection: 'Kết nối miễn phí:',
    connectionCost: 'Chi phí kết nối:',
    yes: 'Có',
    netSuffix: 'trước thuế',
    fovNote: 'Phạm vi FOV hiển thị là gần đúng và có thể thay đổi tùy theo điều kiện lắp đặt.',
  },
  th: {
    offerValidUntil: 'ใบเสนอราคามีผลถึง:',
    placementPlan: 'แผนการติดตั้ง',
    cameras: 'กล้อง',
    recommendations: 'คำแนะนำ:',
    notes: 'หมายเหตุ:',
    offerPreparedBy: 'ใบเสนอราคาจัดทำโดย',
    pricesChangeNotice: 'ราคาอาจเปลี่ยนแปลงหลังวันหมดอายุของใบเสนอราคา',
    yourCompany: 'บริษัทของคุณ',
    no: 'ลำดับ',
    productService: 'สินค้า/บริการ',
    qty: 'จำนวน',
    unitNet: 'ราคาต่อหน่วย (สุทธิ)',
    unitGross: 'ราคาต่อหน่วย (รวม)',
    totalNet: 'รวมสุทธิ',
    totalGross: 'รวมทั้งหมด',
    subtotalNet: 'รวมย่อยสุทธิ:',
    vat: 'ภาษีมูลค่าเพิ่ม:',
    total: 'รวมทั้งสิ้น',
    net: 'สุทธิ',
    gross: 'รวม',
    item: 'รายการ',
    position: 'ตำแหน่ง',
    clientData: 'ข้อมูลลูกค้า',
    clientDetails: 'รายละเอียดลูกค้า',
    projectInformation: 'ข้อมูลโครงการ',
    pricingVariants: 'ตัวเลือกราคา',
    equipmentAndMaterials: 'อุปกรณ์และวัสดุ',
    device: 'อุปกรณ์',
    quantity: 'จำนวน',
    price: 'ราคา',
    date: 'วันที่:',
    company: 'บริษัท:',
    contact: 'ติดต่อ:',
    email: 'อีเมล:',
    phone: 'โทรศัพท์:',
    project: 'โครงการ:',
    fences: 'รั้ว:',
    buildings: 'อาคาร:',
    devices: 'อุปกรณ์:',
    basePrice: 'ราคาพื้นฐาน:',
    perCamera: 'ต่อกล้อง:',
    page: 'หน้า',
    of: 'จาก',
    monthlyFee: 'ค่าบริการรายเดือน:',
    contractPeriod: 'ระยะเวลาสัญญา:',
    indefinite: 'ไม่จำกัด',
    freeConnection: 'เชื่อมต่อฟรี:',
    connectionCost: 'ค่าเชื่อมต่อ:',
    yes: 'ใช่',
    netSuffix: 'สุทธิ',
    fovNote: 'ระยะ FOV ที่แสดงเป็นค่าประมาณและอาจแตกต่างกันตามสภาพการติดตั้ง',
  },
  ja: {
    offerValidUntil: '見積有効期限:',
    placementPlan: '配置計画',
    cameras: 'カメラ',
    recommendations: '推奨事項:',
    notes: '備考:',
    offerPreparedBy: '見積作成者',
    pricesChangeNotice: '見積有効期限後に価格が変更される場合があります。',
    yourCompany: '御社',
    no: '番号',
    productService: '製品/サービス',
    qty: '数量',
    unitNet: '単価（税抜）',
    unitGross: '単価（税込）',
    totalNet: '合計（税抜）',
    totalGross: '合計（税込）',
    subtotalNet: '小計（税抜）:',
    vat: '消費税:',
    total: '合計',
    net: '税抜',
    gross: '税込',
    item: '項目',
    position: '位置',
    clientData: '顧客情報',
    clientDetails: '顧客詳細',
    projectInformation: 'プロジェクト情報',
    pricingVariants: '価格オプション',
    equipmentAndMaterials: '機器・資材',
    device: '機器',
    quantity: '数量',
    price: '価格',
    date: '日付:',
    company: '会社:',
    contact: '連絡先:',
    email: 'メール:',
    phone: '電話:',
    project: 'プロジェクト:',
    fences: 'フェンス:',
    buildings: '建物:',
    devices: '機器:',
    basePrice: '基本価格:',
    perCamera: 'カメラ1台あたり:',
    page: 'ページ',
    of: '/',
    monthlyFee: '月額料金:',
    contractPeriod: '契約期間:',
    indefinite: '無期限',
    freeConnection: '無料接続:',
    connectionCost: '接続費用:',
    yes: 'はい',
    netSuffix: '税抜',
    fovNote: '表示されているFOV範囲は概算値であり、設置条件により異なる場合があります。',
  },
  ko: {
    offerValidUntil: '견적 유효 기한:',
    placementPlan: '배치 계획',
    cameras: '카메라',
    recommendations: '권장 사항:',
    notes: '비고:',
    offerPreparedBy: '견적 작성자',
    pricesChangeNotice: '견적 유효 기간 이후 가격이 변경될 수 있습니다.',
    yourCompany: '귀사',
    no: '번호',
    productService: '제품/서비스',
    qty: '수량',
    unitNet: '단가(세전)',
    unitGross: '단가(세후)',
    totalNet: '합계(세전)',
    totalGross: '합계(세후)',
    subtotalNet: '소계(세전):',
    vat: '부가세:',
    total: '합계',
    net: '세전',
    gross: '세후',
    item: '항목',
    position: '위치',
    clientData: '고객 정보',
    clientDetails: '고객 상세',
    projectInformation: '프로젝트 정보',
    pricingVariants: '가격 옵션',
    equipmentAndMaterials: '장비 및 자재',
    device: '장비',
    quantity: '수량',
    price: '가격',
    date: '날짜:',
    company: '회사:',
    contact: '연락처:',
    email: '이메일:',
    phone: '전화:',
    project: '프로젝트:',
    fences: '펜스:',
    buildings: '건물:',
    devices: '장비:',
    basePrice: '기본 가격:',
    perCamera: '카메라당:',
    page: '페이지',
    of: '/',
    monthlyFee: '월 요금:',
    contractPeriod: '계약 기간:',
    indefinite: '무기한',
    freeConnection: '무료 연결:',
    connectionCost: '연결 비용:',
    yes: '예',
    netSuffix: '세전',
    fovNote: '표시된 FOV 범위는 근사치이며 설치 조건에 따라 달라질 수 있습니다.',
  },
  fr: {
    offerValidUntil: 'Offre valable jusqu\'au :',
    placementPlan: 'Plan de placement',
    cameras: 'Caméras',
    recommendations: 'Recommandations :',
    notes: 'Notes :',
    offerPreparedBy: 'Offre préparée par',
    pricesChangeNotice: 'Les prix peuvent changer après la date de validité de l\'offre.',
    yourCompany: 'Votre Entreprise',
    no: 'N°',
    productService: 'Produit/Service',
    qty: 'Qté',
    unitNet: 'Prix unitaire HT',
    unitGross: 'Prix unitaire TTC',
    totalNet: 'Total HT',
    totalGross: 'Total TTC',
    subtotalNet: 'Sous-total HT :',
    vat: 'TVA :',
    total: 'TOTAL',
    net: 'HT',
    gross: 'TTC',
    item: 'Article',
    position: 'Position',
    clientData: 'DONNÉES CLIENT',
    clientDetails: 'Détails du client',
    projectInformation: 'Informations du projet',
    pricingVariants: 'Variantes de prix',
    equipmentAndMaterials: 'Équipements et matériaux',
    device: 'Appareil',
    quantity: 'Quantité',
    price: 'Prix',
    date: 'Date :',
    company: 'Entreprise :',
    contact: 'Contact :',
    email: 'E-mail :',
    phone: 'Téléphone :',
    project: 'Projet :',
    fences: 'Clôtures :',
    buildings: 'Bâtiments :',
    devices: 'Appareils :',
    basePrice: 'Prix de base :',
    perCamera: 'Par caméra :',
    page: 'Page',
    of: 'sur',
    monthlyFee: 'Frais mensuels :',
    contractPeriod: 'Durée du contrat :',
    indefinite: 'indéterminée',
    freeConnection: 'Connexion gratuite :',
    connectionCost: 'Coût de connexion :',
    yes: 'Oui',
    netSuffix: 'HT',
    fovNote: 'Les portées de FOV indiquées sont approximatives et peuvent varier selon les conditions d\'installation.',
  },
  it: {
    offerValidUntil: 'Offerta valida fino al:',
    placementPlan: 'Piano di posizionamento',
    cameras: 'Telecamere',
    recommendations: 'Raccomandazioni:',
    notes: 'Note:',
    offerPreparedBy: 'Offerta preparata da',
    pricesChangeNotice: 'I prezzi possono cambiare dopo la data di validità dell\'offerta.',
    yourCompany: 'La Vostra Azienda',
    no: 'N.',
    productService: 'Prodotto/Servizio',
    qty: 'Qtà',
    unitNet: 'Prezzo unitario netto',
    unitGross: 'Prezzo unitario lordo',
    totalNet: 'Totale netto',
    totalGross: 'Totale lordo',
    subtotalNet: 'Subtotale netto:',
    vat: 'IVA:',
    total: 'TOTALE',
    net: 'Netto',
    gross: 'Lordo',
    item: 'Articolo',
    position: 'Posizione',
    clientData: 'DATI CLIENTE',
    clientDetails: 'Dettagli cliente',
    projectInformation: 'Informazioni progetto',
    pricingVariants: 'Varianti di prezzo',
    equipmentAndMaterials: 'Attrezzature e materiali',
    device: 'Dispositivo',
    quantity: 'Quantità',
    price: 'Prezzo',
    date: 'Data:',
    company: 'Azienda:',
    contact: 'Contatto:',
    email: 'E-mail:',
    phone: 'Telefono:',
    project: 'Progetto:',
    fences: 'Recinzioni:',
    buildings: 'Edifici:',
    devices: 'Dispositivi:',
    basePrice: 'Prezzo base:',
    perCamera: 'Per telecamera:',
    page: 'Pagina',
    of: 'di',
    monthlyFee: 'Canone mensile:',
    contractPeriod: 'Durata contratto:',
    indefinite: 'indeterminata',
    freeConnection: 'Connessione gratuita:',
    connectionCost: 'Costo di connessione:',
    yes: 'Sì',
    netSuffix: 'netto',
    fovNote: 'I range FOV mostrati sono approssimativi e possono variare in base alle condizioni di installazione.',
  },

  uk: {
    offerValidUntil: 'Пропозиція дійсна до:',
    placementPlan: 'План розміщення',
    cameras: 'Камери',
    recommendations: 'Рекомендації:',
    notes: 'Примітки:',
    offerPreparedBy: 'Пропозицію підготував',
    pricesChangeNotice: 'Ціни можуть змінитися після завершення терміну дії пропозиції.',
    yourCompany: 'Ваша компанія',
    no: '№',
    productService: 'Продукт/Послуга',
    qty: 'Кіл.',
    unitNet: 'Ціна за од. без ПДВ',
    unitGross: 'Ціна за од. з ПДВ',
    totalNet: 'Сума без ПДВ',
    totalGross: 'Сума з ПДВ',
    subtotalNet: 'Проміжний підсумок без ПДВ:',
    vat: 'ПДВ:',
    total: 'РАЗОМ',
    net: 'без ПДВ',
    gross: 'з ПДВ',
    item: 'Позиція',
    position: 'Позиція',
    clientData: 'ДАНІ КЛІЄНТА',
    clientDetails: 'Дані клієнта',
    projectInformation: 'Інформація про проєкт',
    pricingVariants: 'Варіанти цін',
    equipmentAndMaterials: 'Обладнання та матеріали',
    device: 'Пристрій',
    quantity: 'Кількість',
    price: 'Ціна',
    date: 'Дата:',
    company: 'Компанія:',
    contact: 'Контакт:',
    email: 'Email:',
    phone: 'Телефон:',
    project: 'Проєкт:',
    fences: 'Огорожі:',
    buildings: 'Будівлі:',
    devices: 'Пристрої:',
    basePrice: 'Базова ціна:',
    perCamera: 'За камеру:',
    page: 'Сторінка',
    of: 'з',
    monthlyFee: 'Щомісячна плата:',
    contractPeriod: 'Термін договору:',
    indefinite: 'безстроково',
    freeConnection: 'Безкоштовне підключення:',
    connectionCost: 'Вартість підключення:',
    yes: 'Так',
    netSuffix: 'без ПДВ',
    fovNote: 'Показані діапазони FOV є приблизними і можуть відрізнятися залежно від умов монтажу.',
  },
  cs: {
    offerValidUntil: 'Nabídka platná do:',
    placementPlan: 'Plán rozmístění',
    cameras: 'Kamery',
    recommendations: 'Doporučení:',
    notes: 'Poznámky:',
    offerPreparedBy: 'Nabídku vypracoval',
    pricesChangeNotice: 'Ceny se mohou po datu platnosti nabídky změnit.',
    yourCompany: 'Vaše společnost',
    no: 'Č.',
    productService: 'Produkt/Služba',
    qty: 'Mn.',
    unitNet: 'Cena za jednotku bez DPH',
    unitGross: 'Cena za jednotku s DPH',
    totalNet: 'Celkem bez DPH',
    totalGross: 'Celkem s DPH',
    subtotalNet: 'Mezisoučet bez DPH:',
    vat: 'DPH:',
    total: 'CELKEM',
    net: 'bez DPH',
    gross: 's DPH',
    item: 'Položka',
    position: 'Pozice',
    clientData: 'ÚDAJE KLIENTA',
    clientDetails: 'Údaje klienta',
    projectInformation: 'Informace o projektu',
    pricingVariants: 'Varianty cen',
    equipmentAndMaterials: 'Vybavení a materiály',
    device: 'Zařízení',
    quantity: 'Množství',
    price: 'Cena',
    date: 'Datum:',
    company: 'Společnost:',
    contact: 'Kontakt:',
    email: 'E-mail:',
    phone: 'Telefon:',
    project: 'Projekt:',
    fences: 'Oplocení:',
    buildings: 'Budovy:',
    devices: 'Zařízení:',
    basePrice: 'Základní cena:',
    perCamera: 'Za kameru:',
    page: 'Strana',
    of: 'z',
    monthlyFee: 'Měsíční poplatek:',
    contractPeriod: 'Délka smlouvy:',
    indefinite: 'na dobu neurčitou',
    freeConnection: 'Připojení zdarma:',
    connectionCost: 'Cena připojení:',
    yes: 'Ano',
    netSuffix: 'bez DPH',
    fovNote: 'Uvedené rozsahy FOV jsou orientační a mohou se lišit podle podmínek instalace.',
  },
  nl: {
    offerValidUntil: 'Offerte geldig tot:',
    placementPlan: 'Plaatsingsplan',
    cameras: 'Camera\'s',
    recommendations: 'Aanbevelingen:',
    notes: 'Opmerkingen:',
    offerPreparedBy: 'Offerte opgesteld door',
    pricesChangeNotice: 'Prijzen kunnen na de geldigheidsdatum van de offerte wijzigen.',
    yourCompany: 'Uw bedrijf',
    no: 'Nr.',
    productService: 'Product/Dienst',
    qty: 'Aant.',
    unitNet: 'Stuksprijs excl. btw',
    unitGross: 'Stuksprijs incl. btw',
    totalNet: 'Totaal excl. btw',
    totalGross: 'Totaal incl. btw',
    subtotalNet: 'Subtotaal excl. btw:',
    vat: 'BTW:',
    total: 'TOTAAL',
    net: 'excl. btw',
    gross: 'incl. btw',
    item: 'Artikel',
    position: 'Positie',
    clientData: 'KLANTGEGEVENS',
    clientDetails: 'Klantgegevens',
    projectInformation: 'Projectinformatie',
    pricingVariants: 'Prijsvarianten',
    equipmentAndMaterials: 'Apparatuur & Materialen',
    device: 'Apparaat',
    quantity: 'Aantal',
    price: 'Prijs',
    date: 'Datum:',
    company: 'Bedrijf:',
    contact: 'Contact:',
    email: 'E-mail:',
    phone: 'Telefoon:',
    project: 'Project:',
    fences: 'Hekwerken:',
    buildings: 'Gebouwen:',
    devices: 'Apparaten:',
    basePrice: 'Basisprijs:',
    perCamera: 'Per camera:',
    page: 'Pagina',
    of: 'van',
    monthlyFee: 'Maandelijks bedrag:',
    contractPeriod: 'Contractperiode:',
    indefinite: 'voor onbepaalde tijd',
    freeConnection: 'Gratis aansluiting:',
    connectionCost: 'Aansluitkosten:',
    yes: 'Ja',
    netSuffix: 'excl. btw',
    fovNote: 'De getoonde FOV-bereiken zijn indicatief en kunnen variëren afhankelijk van de installatieomstandigheden.',
  },
  hu: {
    offerValidUntil: 'Az ajánlat érvényes:',
    placementPlan: 'Elhelyezési terv',
    cameras: 'Kamerák',
    recommendations: 'Javaslatok:',
    notes: 'Megjegyzések:',
    offerPreparedBy: 'Az ajánlatot készítette',
    pricesChangeNotice: 'Az árak az ajánlat lejárati dátuma után változhatnak.',
    yourCompany: 'Az Ön cége',
    no: 'Sz.',
    productService: 'Termék/Szolgáltatás',
    qty: 'Db',
    unitNet: 'Egységár nettó',
    unitGross: 'Egységár bruttó',
    totalNet: 'Összesen nettó',
    totalGross: 'Összesen bruttó',
    subtotalNet: 'Részösszeg nettó:',
    vat: 'ÁFA:',
    total: 'ÖSSZESEN',
    net: 'nettó',
    gross: 'bruttó',
    item: 'Tétel',
    position: 'Pozíció',
    clientData: 'ÜGYFÉL ADATOK',
    clientDetails: 'Ügyfél adatai',
    projectInformation: 'Projekt információk',
    pricingVariants: 'Árváltozatok',
    equipmentAndMaterials: 'Eszközök és anyagok',
    device: 'Eszköz',
    quantity: 'Mennyiség',
    price: 'Ár',
    date: 'Dátum:',
    company: 'Cég:',
    contact: 'Kapcsolat:',
    email: 'E-mail:',
    phone: 'Telefon:',
    project: 'Projekt:',
    fences: 'Kerítések:',
    buildings: 'Épületek:',
    devices: 'Eszközök:',
    basePrice: 'Alapár:',
    perCamera: 'Kameránként:',
    page: 'Oldal',
    of: 'a/az',
    monthlyFee: 'Havi díj:',
    contractPeriod: 'Szerződés időtartama:',
    indefinite: 'határozatlan',
    freeConnection: 'Ingyenes csatlakozás:',
    connectionCost: 'Csatlakozási költség:',
    yes: 'Igen',
    netSuffix: 'nettó',
    fovNote: 'A megjelenített látómező (FOV) tartományok közelítőek, és a telepítési körülményektől függően változhatnak.',
  },
  lt: {
    offerValidUntil: 'Pasiūlymas galioja iki:',
    placementPlan: 'Išdėstymo planas',
    cameras: 'Kameros',
    recommendations: 'Rekomendacijos:',
    notes: 'Pastabos:',
    offerPreparedBy: 'Pasiūlymą parengė',
    pricesChangeNotice: 'Kainos po pasiūlymo galiojimo pabaigos gali keistis.',
    yourCompany: 'Jūsų įmonė',
    no: 'Nr.',
    productService: 'Prekė / Paslauga',
    qty: 'Kiek.',
    unitNet: 'Vnt. be PVM',
    unitGross: 'Vnt. su PVM',
    totalNet: 'Suma be PVM',
    totalGross: 'Suma su PVM',
    subtotalNet: 'Tarpinė suma be PVM:',
    vat: 'PVM:',
    total: 'IŠ VISO',
    net: 'be PVM',
    gross: 'su PVM',
    item: 'Pozicija',
    position: 'Pozicija',
    clientData: 'KLIENTO DUOMENYS',
    clientDetails: 'Kliento duomenys',
    projectInformation: 'Projekto informacija',
    pricingVariants: 'Kainos variantai',
    equipmentAndMaterials: 'Įranga ir medžiagos',
    device: 'Įrenginys',
    quantity: 'Kiekis',
    price: 'Kaina',
    date: 'Data:',
    company: 'Įmonė:',
    contact: 'Kontaktas:',
    email: 'El. paštas:',
    phone: 'Telefonas:',
    project: 'Projektas:',
    fences: 'Tvoros:',
    buildings: 'Pastatai:',
    devices: 'Įrenginiai:',
    basePrice: 'Bazinė kaina:',
    perCamera: 'Vienai kamerai:',
    page: 'Puslapis',
    of: 'iš',
    monthlyFee: 'Mėnesinis mokestis:',
    contractPeriod: 'Sutarties laikotarpis:',
    indefinite: 'neterminuotai',
    freeConnection: 'Nemokamas prijungimas:',
    connectionCost: 'Prijungimo kaina:',
    yes: 'Taip',
    netSuffix: 'be PVM',
    fovNote: 'Pateikti FOV diapazonai yra apytiksliai ir gali skirtis priklausomai nuo įrengimo sąlygų.',
  },
  ro: {
    offerValidUntil: 'Ofertă valabilă până la:',
    placementPlan: 'Plan de amplasare',
    cameras: 'Camere',
    recommendations: 'Recomandări:',
    notes: 'Note:',
    offerPreparedBy: 'Ofertă pregătită de',
    pricesChangeNotice: 'Prețurile se pot modifica după data de valabilitate a ofertei.',
    yourCompany: 'Compania dvs.',
    no: 'Nr.',
    productService: 'Produs/Serviciu',
    qty: 'Cant.',
    unitNet: 'Preț unitar net',
    unitGross: 'Preț unitar brut',
    totalNet: 'Total net',
    totalGross: 'Total brut',
    subtotalNet: 'Subtotal net:',
    vat: 'TVA:',
    total: 'TOTAL',
    net: 'net',
    gross: 'brut',
    item: 'Articol',
    position: 'Poziție',
    clientData: 'DATE CLIENT',
    clientDetails: 'Date client',
    projectInformation: 'Informații proiect',
    pricingVariants: 'Variante de preț',
    equipmentAndMaterials: 'Echipamente și materiale',
    device: 'Dispozitiv',
    quantity: 'Cantitate',
    price: 'Preț',
    date: 'Data:',
    company: 'Companie:',
    contact: 'Contact:',
    email: 'E-mail:',
    phone: 'Telefon:',
    project: 'Proiect:',
    fences: 'Garduri:',
    buildings: 'Clădiri:',
    devices: 'Dispozitive:',
    basePrice: 'Preț de bază:',
    perCamera: 'Per cameră:',
    page: 'Pagina',
    of: 'din',
    monthlyFee: 'Tarif lunar:',
    contractPeriod: 'Perioada contractuală:',
    indefinite: 'nedeterminat',
    freeConnection: 'Conexiune gratuită:',
    connectionCost: 'Cost conexiune:',
    yes: 'Da',
    netSuffix: 'net',
    fovNote: 'Intervalele FOV afișate sunt aproximative și pot varia în funcție de condițiile de instalare.',
  },
  sv: {
    offerValidUntil: 'Erbjudande giltigt till:',
    placementPlan: 'Placeringsplan',
    cameras: 'Kameror',
    recommendations: 'Rekommendationer:',
    notes: 'Anteckningar:',
    offerPreparedBy: 'Erbjudande utarbetat av',
    pricesChangeNotice: 'Priserna kan ändras efter erbjudandets giltighetsdatum.',
    yourCompany: 'Ditt företag',
    no: 'Nr',
    productService: 'Produkt/Tjänst',
    qty: 'Antal',
    unitNet: 'Pris netto',
    unitGross: 'Pris brutto',
    totalNet: 'Summa netto',
    totalGross: 'Summa brutto',
    subtotalNet: 'Delsumma netto:',
    vat: 'Moms:',
    total: 'TOTALT',
    net: 'Netto',
    gross: 'Brutto',
    item: 'Post',
    position: 'Position',
    clientData: 'KUNDUPPGIFTER',
    clientDetails: 'Kunduppgifter',
    projectInformation: 'Projektinformation',
    pricingVariants: 'Prisvarianter',
    equipmentAndMaterials: 'Utrustning och material',
    device: 'Enhet',
    quantity: 'Kvantitet',
    price: 'Pris',
    date: 'Datum:',
    company: 'Företag:',
    contact: 'Kontakt:',
    email: 'E-post:',
    phone: 'Telefon:',
    project: 'Projekt:',
    fences: 'Stängsel:',
    buildings: 'Byggnader:',
    devices: 'Enheter:',
    basePrice: 'Grundpris:',
    perCamera: 'Per kamera:',
    page: 'Sida',
    of: 'av',
    monthlyFee: 'Månadsavgift:',
    contractPeriod: 'Avtalsperiod:',
    indefinite: 'tills vidare',
    freeConnection: 'Gratis anslutning:',
    connectionCost: 'Anslutningskostnad:',
    yes: 'Ja',
    netSuffix: 'netto',
    fovNote: 'De visade synfältsräckvidderna (FOV) är ungefärliga och kan variera beroende på installationsförhållandena.',
  },
  bg: {
    offerValidUntil: 'Оферта, валидна до:',
    placementPlan: 'План на разположението',
    cameras: 'Камери',
    recommendations: 'Препоръки:',
    notes: 'Бележки:',
    offerPreparedBy: 'Оферта, изготвена от',
    pricesChangeNotice: 'Цените може да се променят след срока на валидност на офертата.',
    yourCompany: 'Вашата фирма',
    no: '№',
    productService: 'Продукт/услуга',
    qty: 'Кол.',
    unitNet: 'Ед. нето',
    unitGross: 'Ед. бруто',
    totalNet: 'Общо нето',
    totalGross: 'Общо бруто',
    subtotalNet: 'Междинна сума нето',
    vat: 'ДДС',
    total: 'Общо',
    net: 'Нето',
    gross: 'Бруто',
    item: 'Позиция',
    position: 'Позиция',
    clientData: 'Данни на клиента',
    clientDetails: 'Данни за клиента',
    projectInformation: 'Информация за проекта',
    pricingVariants: 'Ценови варианти',
    equipmentAndMaterials: 'Оборудване и материали',
    device: 'Устройство',
    quantity: 'Количество',
    price: 'Цена',
    date: 'Дата',
    company: 'Фирма',
    contact: 'Контакт',
    email: 'Имейл',
    phone: 'Телефон',
    project: 'Проект',
    fences: 'Огради',
    buildings: 'Сгради',
    devices: 'Устройства',
    basePrice: 'Базова цена',
    perCamera: 'На камера',
    page: 'Страница',
    of: 'от',
    monthlyFee: 'Месечна такса',
    contractPeriod: 'Срок на договора',
    indefinite: 'безсрочно',
    freeConnection: 'Безплатно свързване:',
    connectionCost: 'Цена за свързване:',
    yes: 'Да',
    netSuffix: 'нето',
    fovNote: 'Показаните обхвати на зрителното поле са приблизителни и може да се различават според условията на монтаж.',
  },
  da: {
    offerValidUntil: 'Tilbud gyldigt til:',
    placementPlan: 'Placeringsplan',
    cameras: 'Kameraer',
    recommendations: 'Anbefalinger:',
    notes: 'Bemærkninger:',
    offerPreparedBy: 'Tilbud udarbejdet af',
    pricesChangeNotice: 'Priser kan ændre sig efter tilbuddets gyldighedsdato.',
    yourCompany: 'Din virksomhed',
    no: 'Nr.',
    productService: 'Produkt/tjeneste',
    qty: 'Antal',
    unitNet: 'Enhed netto',
    unitGross: 'Enhed brutto',
    totalNet: 'I alt netto',
    totalGross: 'I alt brutto',
    subtotalNet: 'Subtotal netto',
    vat: 'Moms',
    total: 'I alt',
    net: 'Netto',
    gross: 'Brutto',
    item: 'Post',
    position: 'Position',
    clientData: 'Kundedata',
    clientDetails: 'Kundeoplysninger',
    projectInformation: 'Projektinformation',
    pricingVariants: 'Prisvarianter',
    equipmentAndMaterials: 'Udstyr og materialer',
    device: 'Enhed',
    quantity: 'Antal',
    price: 'Pris',
    date: 'Dato',
    company: 'Firma',
    contact: 'Kontakt',
    email: 'E-mail',
    phone: 'Telefon',
    project: 'Projekt',
    fences: 'Hegn',
    buildings: 'Bygninger',
    devices: 'Enheder',
    basePrice: 'Grundpris',
    perCamera: 'Pr. kamera',
    page: 'Side',
    of: 'af',
    monthlyFee: 'Månedligt gebyr',
    contractPeriod: 'Kontraktperiode',
    indefinite: 'ubegrænset',
    freeConnection: 'Gratis tilslutning:',
    connectionCost: 'Tilslutningsomkostning:',
    yes: 'Ja',
    netSuffix: 'netto',
    fovNote: 'De viste synsfeltafstande er omtrentlige og kan variere afhængigt af monteringsforholdene.',
  },
  el: {
    offerValidUntil: 'Η προσφορά ισχύει έως:',
    placementPlan: 'Σχέδιο τοποθέτησης',
    cameras: 'Κάμερες',
    recommendations: 'Συστάσεις:',
    notes: 'Σημειώσεις:',
    offerPreparedBy: 'Η προσφορά συντάχθηκε από',
    pricesChangeNotice: 'Οι τιμές ενδέχεται να αλλάξουν μετά τη λήξη ισχύος της προσφοράς.',
    yourCompany: 'Η εταιρεία σας',
    no: 'Αρ.',
    productService: 'Προϊόν/Υπηρεσία',
    qty: 'Ποσ.',
    unitNet: 'Μονάδα καθαρή',
    unitGross: 'Μονάδα μικτή',
    totalNet: 'Σύνολο καθαρό',
    totalGross: 'Σύνολο μικτό',
    subtotalNet: 'Μερικό σύνολο καθαρό',
    vat: 'ΦΠΑ',
    total: 'Σύνολο',
    net: 'Καθαρό',
    gross: 'Μικτό',
    item: 'Είδος',
    position: 'Θέση',
    clientData: 'Στοιχεία πελάτη',
    clientDetails: 'Λεπτομέρειες πελάτη',
    projectInformation: 'Πληροφορίες έργου',
    pricingVariants: 'Παραλλαγές τιμών',
    equipmentAndMaterials: 'Εξοπλισμός και υλικά',
    device: 'Συσκευή',
    quantity: 'Ποσότητα',
    price: 'Τιμή',
    date: 'Ημερομηνία',
    company: 'Εταιρεία',
    contact: 'Επικοινωνία',
    email: 'Email',
    phone: 'Τηλέφωνο',
    project: 'Έργο',
    fences: 'Περιφράξεις',
    buildings: 'Κτίρια',
    devices: 'Συσκευές',
    basePrice: 'Βασική τιμή',
    perCamera: 'Ανά κάμερα',
    page: 'Σελίδα',
    of: 'από',
    monthlyFee: 'Μηνιαία χρέωση',
    contractPeriod: 'Διάρκεια σύμβασης',
    indefinite: 'αόριστη',
    freeConnection: 'Δωρεάν σύνδεση:',
    connectionCost: 'Κόστος σύνδεσης:',
    yes: 'Ναι',
    netSuffix: 'καθαρό',
    fovNote: 'Οι εμφανιζόμενες αποστάσεις οπτικού πεδίου είναι κατά προσέγγιση και ενδέχεται να διαφέρουν ανάλογα με τις συνθήκες εγκατάστασης.',
  },
  et: {
    offerValidUntil: 'Pakkumine kehtib kuni:',
    placementPlan: 'Paigutusplaan',
    cameras: 'Kaamerad',
    recommendations: 'Soovitused:',
    notes: 'Märkused:',
    offerPreparedBy: 'Pakkumise koostas',
    pricesChangeNotice: 'Hinnad võivad pärast pakkumise kehtivusaega muutuda.',
    yourCompany: 'Teie ettevõte',
    no: 'Nr',
    productService: 'Toode/teenus',
    qty: 'Kogus',
    unitNet: 'Ühik neto',
    unitGross: 'Ühik bruto',
    totalNet: 'Kokku neto',
    totalGross: 'Kokku bruto',
    subtotalNet: 'Vahesumma neto',
    vat: 'Käibemaks',
    total: 'Kokku',
    net: 'Neto',
    gross: 'Bruto',
    item: 'Kirje',
    position: 'Positsioon',
    clientData: 'Kliendi andmed',
    clientDetails: 'Kliendi üksikasjad',
    projectInformation: 'Projekti teave',
    pricingVariants: 'Hinnavariandid',
    equipmentAndMaterials: 'Seadmed ja materjalid',
    device: 'Seade',
    quantity: 'Kogus',
    price: 'Hind',
    date: 'Kuupäev',
    company: 'Ettevõte',
    contact: 'Kontakt',
    email: 'E-post',
    phone: 'Telefon',
    project: 'Projekt',
    fences: 'Piirded',
    buildings: 'Hooned',
    devices: 'Seadmed',
    basePrice: 'Baashind',
    perCamera: 'Kaamera kohta',
    page: 'Lehekülg',
    of: '/',
    monthlyFee: 'Kuutasu',
    contractPeriod: 'Lepingu periood',
    indefinite: 'tähtajatu',
    freeConnection: 'Tasuta ühendus:',
    connectionCost: 'Ühenduse maksumus:',
    yes: 'Jah',
    netSuffix: 'neto',
    fovNote: 'Näidatud vaatevälja ulatused on ligikaudsed ja võivad paigaldustingimustest sõltuvalt erineda.',
  },
  fi: {
    offerValidUntil: 'Tarjous voimassa:',
    placementPlan: 'Sijoittelusuunnitelma',
    cameras: 'Kamerat',
    recommendations: 'Suositukset:',
    notes: 'Huomautukset:',
    offerPreparedBy: 'Tarjouksen laati',
    pricesChangeNotice: 'Hinnat voivat muuttua tarjouksen voimassaolon päätyttyä.',
    yourCompany: 'Yrityksesi',
    no: 'Nro',
    productService: 'Tuote/palvelu',
    qty: 'Määrä',
    unitNet: 'Yksikkö netto',
    unitGross: 'Yksikkö brutto',
    totalNet: 'Yhteensä netto',
    totalGross: 'Yhteensä brutto',
    subtotalNet: 'Välisumma netto',
    vat: 'ALV',
    total: 'Yhteensä',
    net: 'Netto',
    gross: 'Brutto',
    item: 'Rivi',
    position: 'Sijainti',
    clientData: 'Asiakastiedot',
    clientDetails: 'Asiakkaan tiedot',
    projectInformation: 'Projektin tiedot',
    pricingVariants: 'Hintavaihtoehdot',
    equipmentAndMaterials: 'Laitteet ja materiaalit',
    device: 'Laite',
    quantity: 'Määrä',
    price: 'Hinta',
    date: 'Päivämäärä',
    company: 'Yritys',
    contact: 'Yhteyshenkilö',
    email: 'Sähköposti',
    phone: 'Puhelin',
    project: 'Projekti',
    fences: 'Aidat',
    buildings: 'Rakennukset',
    devices: 'Laitteet',
    basePrice: 'Perushinta',
    perCamera: 'Kameraa kohden',
    page: 'Sivu',
    of: '/',
    monthlyFee: 'Kuukausimaksu',
    contractPeriod: 'Sopimuskausi',
    indefinite: 'toistaiseksi',
    freeConnection: 'Ilmainen liitäntä:',
    connectionCost: 'Liitännän hinta:',
    yes: 'Kyllä',
    netSuffix: 'netto',
    fovNote: 'Esitetyt kuvakulman kantamat ovat likimääräisiä ja voivat vaihdella asennusolosuhteiden mukaan.',
  },
  hr: {
    offerValidUntil: 'Ponuda vrijedi do:',
    placementPlan: 'Plan razmještaja',
    cameras: 'Kamere',
    recommendations: 'Preporuke:',
    notes: 'Napomene:',
    offerPreparedBy: 'Ponudu izradio',
    pricesChangeNotice: 'Cijene se mogu promijeniti nakon isteka valjanosti ponude.',
    yourCompany: 'Vaša tvrtka',
    no: 'Br.',
    productService: 'Proizvod/usluga',
    qty: 'Kol.',
    unitNet: 'Jedinična neto',
    unitGross: 'Jedinična bruto',
    totalNet: 'Ukupno neto',
    totalGross: 'Ukupno bruto',
    subtotalNet: 'Međuzbroj neto',
    vat: 'PDV',
    total: 'Ukupno',
    net: 'Neto',
    gross: 'Bruto',
    item: 'Stavka',
    position: 'Pozicija',
    clientData: 'Podaci o klijentu',
    clientDetails: 'Podaci klijenta',
    projectInformation: 'Informacije o projektu',
    pricingVariants: 'Varijante cijena',
    equipmentAndMaterials: 'Oprema i materijali',
    device: 'Uređaj',
    quantity: 'Količina',
    price: 'Cijena',
    date: 'Datum',
    company: 'Tvrtka',
    contact: 'Kontakt',
    email: 'E-pošta',
    phone: 'Telefon',
    project: 'Projekt',
    fences: 'Ograde',
    buildings: 'Zgrade',
    devices: 'Uređaji',
    basePrice: 'Osnovna cijena',
    perCamera: 'Po kameri',
    page: 'Stranica',
    of: 'od',
    monthlyFee: 'Mjesečna naknada',
    contractPeriod: 'Razdoblje ugovora',
    indefinite: 'neodređeno',
    freeConnection: 'Besplatno spajanje:',
    connectionCost: 'Trošak spajanja:',
    yes: 'Da',
    netSuffix: 'neto',
    fovNote: 'Prikazani dosezi vidnog polja su približni i mogu se razlikovati ovisno o uvjetima ugradnje.',
  },
  lv: {
    offerValidUntil: 'Piedāvājums derīgs līdz:',
    placementPlan: 'Izvietojuma plāns',
    cameras: 'Kameras',
    recommendations: 'Ieteikumi:',
    notes: 'Piezīmes:',
    offerPreparedBy: 'Piedāvājumu sagatavoja',
    pricesChangeNotice: 'Cenas pēc piedāvājuma derīguma termiņa var mainīties.',
    yourCompany: 'Jūsu uzņēmums',
    no: 'Nr.',
    productService: 'Prece/pakalpojums',
    qty: 'Sk.',
    unitNet: 'Vienība neto',
    unitGross: 'Vienība bruto',
    totalNet: 'Kopā neto',
    totalGross: 'Kopā bruto',
    subtotalNet: 'Starpsumma neto',
    vat: 'PVN',
    total: 'Kopā',
    net: 'Neto',
    gross: 'Bruto',
    item: 'Pozīcija',
    position: 'Pozīcija',
    clientData: 'Klienta dati',
    clientDetails: 'Klienta informācija',
    projectInformation: 'Projekta informācija',
    pricingVariants: 'Cenu varianti',
    equipmentAndMaterials: 'Aprīkojums un materiāli',
    device: 'Ierīce',
    quantity: 'Daudzums',
    price: 'Cena',
    date: 'Datums',
    company: 'Uzņēmums',
    contact: 'Kontaktpersona',
    email: 'E-pasts',
    phone: 'Tālrunis',
    project: 'Projekts',
    fences: 'Žogi',
    buildings: 'Ēkas',
    devices: 'Ierīces',
    basePrice: 'Bāzes cena',
    perCamera: 'Par kameru',
    page: 'Lappuse',
    of: 'no',
    monthlyFee: 'Ikmēneša maksa',
    contractPeriod: 'Līguma periods',
    indefinite: 'beztermiņa',
    freeConnection: 'Bezmaksas pieslēgums:',
    connectionCost: 'Pieslēguma izmaksas:',
    yes: 'Jā',
    netSuffix: 'neto',
    fovNote: 'Norādītie redzes lauka attālumi ir aptuveni un var atšķirties atkarībā no uzstādīšanas apstākļiem.',
  },
  sk: {
    offerValidUntil: 'Ponuka platná do:',
    placementPlan: 'Plán rozmiestnenia',
    cameras: 'Kamery',
    recommendations: 'Odporúčania:',
    notes: 'Poznámky:',
    offerPreparedBy: 'Ponuku vypracoval',
    pricesChangeNotice: 'Ceny sa po uplynutí platnosti ponuky môžu zmeniť.',
    yourCompany: 'Vaša firma',
    no: 'Č.',
    productService: 'Produkt/služba',
    qty: 'Ks',
    unitNet: 'Jednotka netto',
    unitGross: 'Jednotka brutto',
    totalNet: 'Spolu netto',
    totalGross: 'Spolu brutto',
    subtotalNet: 'Medzisúčet netto',
    vat: 'DPH',
    total: 'Spolu',
    net: 'Netto',
    gross: 'Brutto',
    item: 'Položka',
    position: 'Pozícia',
    clientData: 'Údaje klienta',
    clientDetails: 'Údaje o klientovi',
    projectInformation: 'Informácie o projekte',
    pricingVariants: 'Cenové varianty',
    equipmentAndMaterials: 'Zariadenia a materiál',
    device: 'Zariadenie',
    quantity: 'Množstvo',
    price: 'Cena',
    date: 'Dátum',
    company: 'Firma',
    contact: 'Kontakt',
    email: 'E-mail',
    phone: 'Telefón',
    project: 'Projekt',
    fences: 'Ploty',
    buildings: 'Budovy',
    devices: 'Zariadenia',
    basePrice: 'Základná cena',
    perCamera: 'Na kameru',
    page: 'Strana',
    of: 'z',
    monthlyFee: 'Mesačný poplatok',
    contractPeriod: 'Obdobie zmluvy',
    indefinite: 'na neurčito',
    freeConnection: 'Bezplatné pripojenie:',
    connectionCost: 'Cena pripojenia:',
    yes: 'Áno',
    netSuffix: 'netto',
    fovNote: 'Uvedené dosahy zorného poľa sú približné a môžu sa líšiť podľa podmienok montáže.',
  },
  sl: {
    offerValidUntil: 'Ponudba velja do:',
    placementPlan: 'Načrt razmestitve',
    cameras: 'Kamere',
    recommendations: 'Priporočila:',
    notes: 'Opombe:',
    offerPreparedBy: 'Ponudbo pripravil',
    pricesChangeNotice: 'Cene se po izteku veljavnosti ponudbe lahko spremenijo.',
    yourCompany: 'Vaše podjetje',
    no: 'Št.',
    productService: 'Izdelek/storitev',
    qty: 'Kol.',
    unitNet: 'Enota neto',
    unitGross: 'Enota bruto',
    totalNet: 'Skupaj neto',
    totalGross: 'Skupaj bruto',
    subtotalNet: 'Vmesni seštevek neto',
    vat: 'DDV',
    total: 'Skupaj',
    net: 'Neto',
    gross: 'Bruto',
    item: 'Postavka',
    position: 'Pozicija',
    clientData: 'Podatki stranke',
    clientDetails: 'Podrobnosti stranke',
    projectInformation: 'Informacije o projektu',
    pricingVariants: 'Cenovne različice',
    equipmentAndMaterials: 'Oprema in materiali',
    device: 'Naprava',
    quantity: 'Količina',
    price: 'Cena',
    date: 'Datum',
    company: 'Podjetje',
    contact: 'Kontakt',
    email: 'E-pošta',
    phone: 'Telefon',
    project: 'Projekt',
    fences: 'Ograje',
    buildings: 'Stavbe',
    devices: 'Naprave',
    basePrice: 'Osnovna cena',
    perCamera: 'Na kamero',
    page: 'Stran',
    of: 'od',
    monthlyFee: 'Mesečna naročnina',
    contractPeriod: 'Obdobje pogodbe',
    indefinite: 'nedoločen',
    freeConnection: 'Brezplačna priključitev:',
    connectionCost: 'Strošek priključitve:',
    yes: 'Da',
    netSuffix: 'neto',
    fovNote: 'Prikazani dosegi vidnega polja so približni in se lahko razlikujejo glede na pogoje namestitve.',
  },
};

// Get PDF text translation for a language
export const getPDFText = (language: SupportedLanguage, key: string): string => {
  return PDF_TEXT_TRANSLATIONS[language]?.[key] || PDF_TEXT_TRANSLATIONS.en[key] || key;
};
